import { $el } from '../ui/elements';
import { $cdn } from '../y/cdn';
import { $log } from '../y/debug';
import { yLoadImage, yReadFile } from '../y/file-utils';
import { $http } from '../y/http';
import { YId } from '../y/types';

export interface SImageInfo {
    type: string;
    animated: boolean;
    width: number;
    height: number;
    quality?: number;
    transparent: boolean;
}

export interface SUploadVariant {
    key: string;
    length: number;
    type: string;
    image_info?: SImageInfo;
}

export interface SUploadResponse {
    id: YId;
    scope: string;
    variants: SUploadVariant[];
}

export type YProgressCallback = (p: number) => void;

const IMAGE_MIMETYPE = /^image\//;

function stringFromBytes(buffer: DataView, start: number, length: number) {
    let dst = '';
    for(let i = 0; i < length; i++) {
        dst += String.fromCharCode(buffer.getUint8(start + i));
    }
    return dst;
}

function orientationFromBytes(file: ArrayBuffer) {
    const data_view = new DataView(file);

    if(data_view.getUint8(0) != 0xFF || data_view.getUint8(1) != 0xD8) //not a JPEG file
        return 1;

    const length = file.byteLength;
    let chunk: number;

    for(let offset = 2; offset < length; offset += chunk + 2) {
        if(data_view.getUint8(offset) != 0xFF) //invalid marker
            return 1;

        chunk = data_view.getUint16(offset + 2);

        if(data_view.getUint8(offset + 1) == 0xE1) //EXIF marker
        {
            offset += 4;
            if(stringFromBytes(data_view, offset, 4) != 'Exif') //Invalid EXIF data
                return 1;

            offset += 6;

            const endian_marker = data_view.getUint16(offset);
            let little_endian: boolean;

            if(endian_marker == 0x4949)
                little_endian = true;
            else if(endian_marker == 0x4d4d)
                little_endian = false;
            else //Invalid marker
                return 1;

            if(data_view.getUint16(offset + 2, little_endian) != 0x002a) //Invalid marker
                return 1;

            const first_offset = data_view.getUint32(offset + 4, little_endian);

            if(first_offset < 8) //Invalid offset
                return 1;

            offset += first_offset;

            const entries = data_view.getUint16(offset, little_endian);

            for(let i = 0; i < entries; i++) {
                const entry_offset = offset + i * 12 + 2;

                if(data_view.getUint16(entry_offset, little_endian) == 0x0112) //Orientation tag
                {
                    const orientation = data_view.getUint16(entry_offset + 8, little_endian);

                    if(!orientation || orientation < 1 || orientation > 8)
                        return 1;

                    return orientation;
                }
            }
        }
    }

    return 1;
}

function orientationFromDataURL(data_url: string, mime_type: string) {
    if(mime_type !== 'image/jpeg') return 1;

    const byte_string = dataURLToByteString(data_url);

    return orientationFromBytes(byteStringToArrayBuffer(byte_string));
}

function dataURLToByteString(data_url: string) {
    const split = data_url.split(',');
    if(split[0].includes('base64'))
        return atob(split[1]);
    else
        return decodeURI(split[1]);
}

function byteStringToArrayBuffer(byte_string: string) {
    const buffer = new ArrayBuffer(byte_string.length);
    const view = new Uint8Array(buffer);
    for(let i = 0; i < byte_string.length; i++) {
        view[i] = byte_string.charCodeAt(i);
    }

    return buffer;
}

function dataURLToBlob(data_url: string, mime_type: string) {
    const byte_string = dataURLToByteString(data_url);
    return new Blob([byteStringToArrayBuffer(byte_string)], { type: mime_type });
}

export function sUpload(file: File | Blob, scope: string, progress?: (p: number) => void, progress_start: number = 0) {
    progress_start = progress_start | 0;
    const progress_cb = progress && ((p: number) => { progress(progress_start * (1 - p) + p) });
    return $http.POST<SUploadResponse>($cdn.upload_url, file, { up_progress: progress_cb, aux: { scope: scope } });
}

interface UploadPictureOptions {
    square?: boolean;
    max_width?: number;
    max_height?: number;
    type?: string;
}

export async function sUploadImage(file: Blob, scope: string, options: UploadPictureOptions = {}, progress?: YProgressCallback) {
    let data_url = await yReadFile(file, 'url');

    if(options.type && file.type != options.type)
        file = new Blob([file], { type: options.type });
    else if(!file.type)
        file = new Blob([file], { type: 'image/jpeg' });

    if(IMAGE_MIMETYPE.test(file.type)) {//it's a real image
        const image = await yLoadImage(data_url);

        const orientation = orientationFromDataURL(data_url, file.type);
        let must_redo = (orientation !== 1);
        let must_png = false;
        let changed_ratio = false, ratio_x = 0, ratio_y = 0, ratio_w = 1, ratio_h = 1;
        let canvas_width = image.width;
        let canvas_height = image.height;

        if(file.type === 'image/gif') {
            must_redo = false;
        } else {
            if(options.square) {
                if(image.width < image.height) {
                    ratio_x = (image.height - image.width) / (2 * image.height);
                    ratio_w = image.width / image.height;
                    canvas_width = image.height;
                    must_redo = true;
                    must_png = true;
                    changed_ratio = true;
                }
                else if(image.width > image.height) {
                    ratio_y = (image.width - image.height) / (2 * image.width);
                    ratio_h = image.height / image.width;
                    canvas_height = image.width;
                    must_redo = true;
                    must_png = true;
                    changed_ratio = true;
                }
            }

            options.max_width = options.max_width || 1920;
            options.max_height = options.max_height || 1080;

            if((canvas_width > options.max_width * 1.2 && canvas_height > options.max_height * 1.2) || file.size > 1500000) //some tolerance... no need to resize for small gain
            {
                const factor = Math.max(1, Math.min(canvas_width / options.max_width, canvas_height / options.max_height));
                canvas_width = Math.round(canvas_width / factor);
                canvas_height = Math.round(canvas_height / factor);

                must_redo = true;
            }
        }

        if(must_redo) {
            progress && progress(0.01);

            const canvas = $el.CANVAS(canvas_width, canvas_height);
            const ctx = canvas.getContext('2d')!;

            if(orientation === 2)
                ctx.transform(-1, 0, 0, 1, canvas.width, 0);
            else if(orientation === 3)
                ctx.transform(-1, 0, 0, -1, canvas.width, canvas.height);
            else if(orientation === 4)
                ctx.transform(1, 0, 0, -1, 0, canvas.height);
            else if(orientation === 5)
                ctx.transform(0, 1, 1, 0, 0, 0);
            else if(orientation === 6)
                ctx.transform(0, 1, -1, 0, canvas.height, 0);
            else if(orientation === 7)
                ctx.transform(0, -1, -1, 0, canvas.height, canvas.width);
            else if(orientation === 8)
                ctx.transform(0, -1, 1, 0, 0, canvas.width);

            if(changed_ratio) {
                ctx.drawImage(image, Math.round(ratio_x * canvas_width), Math.round(ratio_y * canvas_height),
                    Math.round(ratio_w * canvas_width), Math.round(ratio_h * canvas_height));
            } else {
                ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
            }

            const file_type = (must_png || (file.type == 'image/png' && file.size < 1500000)) ? 'image/png' : 'image/jpeg';

            data_url = canvas.toDataURL(file_type);
            file = dataURLToBlob(data_url, file_type);
        }

        const response = await sUpload(file, scope, progress, 0.25);
        const info = response.variants.find(v => v.key === 'default')?.image_info;
        if(info)
            return { id: response.id, info };

        throw 'Invalid response';
    }

    $log('buola', 'It\'s not a picture!!!!');
    throw 'Invalid file format';
    // upload<UploadPictureResponse>(file, scope, progress, 0, { convert: true, max_width: options.max_width||1024, 
    //                                                    max_height: options.max_height||768 }).then((response) => {
    //     resolve(response.id);
    // }, reject);
}
